import type { Cita, HorasEfectivas, Pase } from '../../api/ops/citas';
import { dinero } from '../../logica/dinero';
import type { EstadoChip } from '../../componentes/Chip';
import { aMinutos, diaDeLaSemana, enZona, sumarDias } from '../../logica/horario';
import { CANAL, ESTADO } from './textos';

/**
 * Lógica de la Agenda sin pantalla (para poder probarla): fechas en la zona del negocio, la franja de cada cita, los
 * huecos libres entre los tramos del profesional y lo que se puede hacer con una cita según lo que diga la API
 * (`actions`). Nunca calcula dinero: los importes llegan de la API y solo se escriben con `logica/dinero.ts`.
 */

export const DIA_MIN = 1440;
const DIAS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'] as const;
const DIAS_CORTOS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'] as const;
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'] as const;
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'] as const;

const dos = (n: number) => String(n).padStart(2, '0');

/** 570 → «09:30». */
export function hm(minutos: number): string {
    const m = ((minutos % DIA_MIN) + DIA_MIN) % DIA_MIN;
    return `${dos(Math.floor(m / 60))}:${dos(m % 60)}`;
}

export const mayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const partes = (iso: string) => iso.split('-').map(Number) as [number, number, number];

/** «mié 14 oct». */
export function diaCorto(iso: string): string {
    const [, m, d] = partes(iso);
    return `${DIAS_CORTOS[diaDeLaSemana(iso)] ?? ''} ${String(d)} ${MESES_CORTOS[m - 1] ?? ''}`;
}

/** «Miércoles, 14 de octubre». */
export function diaLargo(iso: string): string {
    const [, m, d] = partes(iso);
    return `${mayuscula(DIAS[diaDeLaSemana(iso)] ?? '')}, ${String(d)} de ${MESES[m - 1] ?? ''}`;
}

/** «Hoy», «Ayer», «Mañana» o «Mié 14 oct». */
export function relativo(iso: string, hoy: string): string {
    if (iso === hoy) {
        return 'Hoy';
    }
    if (iso === sumarDias(hoy, -1)) {
        return 'Ayer';
    }
    if (iso === sumarDias(hoy, 1)) {
        return 'Mañana';
    }
    return mayuscula(diaCorto(iso));
}

/** Lunes de la semana de `iso`. */
export function lunesDe(iso: string): string {
    return sumarDias(iso, -diaDeLaSemana(iso));
}

/** «12 al 18 de octubre». */
export function textoSemana(lunes: string): string {
    const fin = sumarDias(lunes, 6);
    return `${String(partes(lunes)[2])} al ${String(partes(fin)[2])} de ${MESES[partes(fin)[1] - 1] ?? ''}`;
}

/** «45 min», «1 h», «1 h 15 min». */
export function duracion(minutos: number): string {
    if (minutos < 60) {
        return `${String(minutos)} min`;
    }
    return minutos % 60 === 0 ? `${String(minutos / 60)} h` : `${String(Math.floor(minutos / 60))} h ${String(minutos % 60)} min`;
}

export function primerNombre(nombre: string): string {
    return nombre.trim().split(/\s+/)[0] ?? nombre;
}

export function iniciales(nombre: string): string {
    const p = nombre.trim().split(/\s+/).filter((x) => x !== '');
    return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toUpperCase();
}

/** El «ahora» en la zona del negocio: su fecha y sus minutos desde las 00:00. */
export interface Ahora {
    fecha: string;
    minutos: number;
}

export function ahoraEn(zona: string | null | undefined, ahora: Date = new Date()): Ahora {
    const z = enZona(ahora.toISOString(), zona);
    return { fecha: z.fecha, minutos: aMinutos(z.hora) };
}

/** Hora local de un instante en la zona del negocio («10:12»). */
export function horaEn(instante: string | null | undefined, zona: string | null | undefined): string {
    return instante == null ? '' : enZona(instante, zona).hora;
}

/** «Hoy, 10:12», «Ayer, 19:00» o «12 oct, 18:40» de un instante. */
export function momento(instante: string | null | undefined, zona: string | null | undefined, hoy: string): string {
    if (instante == null) {
        return '';
    }
    const z = enZona(instante, zona);
    if (z.fecha === hoy || z.fecha === sumarDias(hoy, -1)) {
        return `${relativo(z.fecha, hoy)}, ${z.hora}`;
    }
    const [, m, d] = partes(z.fecha);
    return `${String(d)} ${MESES_CORTOS[m - 1] ?? ''}, ${z.hora}`;
}

/** Franja local de la cita: su día y sus minutos de inicio y fin (el fin, con la duración que da la API). */
export interface Franja {
    fecha: string;
    inicio: number;
    fin: number;
}

export function franjaDe(c: Pick<Cita, 'local_date' | 'local_time' | 'total_duration_minutes'>): Franja {
    const inicio = aMinutos(c.local_time);
    return { fecha: c.local_date, inicio, fin: inicio + c.total_duration_minutes };
}

/** «Corte + Tinte». */
export function textoServicios(c: Pick<Cita, 'segments'>): string {
    return c.segments.map((s) => s.service_name ?? '').filter((x) => x !== '').join(' + ');
}

export function activa(c: Pick<Cita, 'status'>): boolean {
    return c.status !== 'cancelled' && c.status !== 'expired';
}

/** Confirmada y ya empezada, hoy: «Ahora». */
export function enCurso(c: Cita, ahora: Ahora): boolean {
    const f = franjaDe(c);
    return c.status === 'confirmed' && f.fecha === ahora.fecha && f.inicio <= ahora.minutos && ahora.minutos < f.fin;
}

/** Ya terminó (su franja acabó). */
export function terminada(c: Cita, ahora: Ahora): boolean {
    const f = franjaDe(c);
    return f.fecha < ahora.fecha || (f.fecha === ahora.fecha && f.fin <= ahora.minutos);
}

export interface ChipCita {
    texto: string;
    estado: EstadoChip;
}

/** Los chips de una cita en la agenda: el estado si no es el normal, el cobro fallido y la firma pendiente. */
export function chipsDe(c: Cita, ahora: Ahora, max = 3): ChipCita[] {
    const chips: ChipCita[] = [];
    if (enCurso(c, ahora)) {
        chips.push({ texto: 'Ahora', estado: 'live' });
    } else if (c.status !== 'confirmed' && c.status !== 'arrived' && c.status !== 'completed') {
        const [texto, estado] = ESTADO[c.status];
        chips.push({ texto, estado });
    }
    if (c.payment_status === 'charge_failed') {
        chips.push({ texto: 'Cobro fallido', estado: 'problem' });
    }
    if (c.consent_status === 'pending' && activa(c)) {
        chips.push({ texto: 'Firma pendiente', estado: 'pending' });
    }
    return chips.slice(0, max);
}

/** El estado que se ve en la ficha (con «Ahora» si está en curso). */
export function estadoVisible(c: Cita, ahora: Ahora): readonly [string, EstadoChip] {
    return enCurso(c, ahora) ? ['Ahora', 'live'] : ESTADO[c.status];
}

/**
 * ¿Se puede marcar ya «No vino»? Por completar, siempre; confirmada, desde `can_mark_no_show_from` de la API (inicio más
 * el margen del negocio, G322). Nunca lo decide el reloj del panel solo.
 */
export function puedeNoVino(c: Cita, ahora: Date = new Date()): boolean {
    if (c.status === 'pending_completion') {
        return true;
    }
    const desde = c.actions.can_mark_no_show_from;
    return c.status === 'confirmed' && desde != null && new Date(desde).getTime() <= ahora.getTime();
}

/** ¿Se puede registrar la llegada? Confirmada y de hoy. */
export function puedeLlegada(c: Cita, hoy: string): boolean {
    return c.status === 'confirmed' && c.local_date === hoy;
}

/** «Web», «Panel»… */
export function canal(c: Pick<Cita, 'channel'>): string {
    return CANAL[c.channel] ?? c.channel;
}

/** Los tramos de un día de las horas efectivas, en minutos (los de madrugada, después de 1440). */
export function tramosDelDia(horas: HorasEfectivas | undefined, fecha: string): [number, number][] {
    const dia = horas?.days.find((d) => d.local_date === fecha);
    if (dia === undefined || dia.status === 'closed' || dia.status === 'holiday') {
        return [];
    }
    return dia.intervals.map((i) => {
        const desde = aMinutos(i.start_local) + (i.starts_next_day === true ? DIA_MIN : 0);
        let hasta = aMinutos(i.end_local) + (i.ends_next_day === true ? DIA_MIN : 0);
        if (hasta <= desde) {
            hasta += DIA_MIN;
        }
        return [desde, hasta] as [number, number];
    });
}

/** ¿Sabemos si ese día trabaja? (sin horas efectivas no se puede decir). */
export function hayDia(horas: HorasEfectivas | undefined, fecha: string): boolean {
    return horas?.days.some((d) => d.local_date === fecha) === true;
}

/**
 * Huecos libres de un profesional un día: sus tramos menos sus citas activas, desde «ahora» si es hoy (redondeado al
 * cuarto de hora) y de 30 minutos o más, como en el diseño. Solo para pintar: la cita la valida la API.
 */
export function huecosLibres(tramos: readonly [number, number][], citas: readonly Cita[], fecha: string, ahora: Ahora, minimo = 30): [number, number][] {
    if (fecha < ahora.fecha) {
        return [];
    }
    const desde = fecha === ahora.fecha ? Math.ceil(ahora.minutos / 15) * 15 : 0;
    const ocupadas = citas
        .filter(activa)
        .map(franjaDe)
        .filter((f) => f.fecha === fecha)
        .map((f) => [f.inicio, f.fin] as [number, number])
        .sort((a, b) => a[0] - b[0]);
    const libres: [number, number][] = [];
    for (const [s, e] of tramos) {
        let t = Math.max(s, desde);
        for (const [os, oe] of ocupadas) {
            if (oe <= s || os >= e) {
                continue;
            }
            if (os > t) {
                libres.push([t, Math.min(os, e)]);
            }
            t = Math.max(t, oe);
        }
        if (t < e) {
            libres.push([t, e]);
        }
    }
    return libres.filter(([a, b]) => b - a >= minimo);
}

/** Lo que dice la fila «Pase» de la ficha (G415, G480). */
export function lineaPase(c: Cita, pase: Pase | null, profesional: string): { valor: string; sub: string } {
    if (c.status === 'cancelled' || c.status === 'expired' || pase?.status === 'voided') {
        return { valor: 'Anulado', sub: 'Ya no vale en la puerta' };
    }
    if (c.status === 'pending_payment' || pase === null) {
        return { valor: 'Al pagar la señal', sub: 'Le llega con la confirmación' };
    }
    return { valor: pase.seq > 1 ? 'Nuevo, tras el cambio' : 'Enviado', sub: `Con su QR registras la llegada · de ${primerNombre(profesional)}` };
}

/** Teléfono enmascarado para el texto de «Reenviar el enlace de pago»: «+34 66** *** 823». */
export function telefonoOculto(tel: string | null | undefined): string {
    if (tel == null || tel.length < 6) {
        return '';
    }
    const limpio = tel.replace(/\s+/g, '');
    return `${limpio.slice(0, 3)} ${limpio.slice(3, 5)}** *** ${limpio.slice(-3)}`;
}

/** «+34 664 268 823» de un E.164 español; los demás, tal cual. */
export function telefonoLegible(tel: string | null | undefined): string {
    if (tel == null) {
        return '';
    }
    const m = /^\+34(\d{3})(\d{3})(\d{3})$/.exec(tel);
    return m === null ? tel : `+34 ${m[1] ?? ''} ${m[2] ?? ''} ${m[3] ?? ''}`;
}

/** Todas las páginas de una lista por cursor (como mucho `max`). */
export async function todas<T>(pedir: (cursor: string | undefined) => Promise<{ data: T[]; next_cursor: string | null }>, max = 10): Promise<T[]> {
    const fuera: T[] = [];
    let cursor: string | undefined;
    for (let i = 0; i < max; i++) {
        const p = await pedir(cursor);
        fuera.push(...p.data);
        if (p.next_cursor === null) {
            break;
        }
        cursor = p.next_cursor;
    }
    return fuera;
}

/** El texto de un error para el aviso breve: el de la API (`detail`), o el genérico. */
export function textoDeError(e: unknown, generico = 'No se ha podido hacer. Prueba de nuevo.'): string {
    if (typeof e === 'object' && e !== null && 'problema' in e) {
        const p = (e as { problema: { detail?: string; title?: string } }).problema;
        return p.detail ?? p.title ?? generico;
    }
    return generico;
}

/** El cargo por no presentarse que la API ha pedido al marcarlo (si la política lo cobra). */
export function cargoNoShow(c: Cita) {
    return (c.payments ?? []).find((p) => p.concept === 'no_show_fee') ?? null;
}

/** Lo que se cuenta tras marcar «No vino», con lo que devolvió la API (nunca un importe calculado aquí). */
export function textoTrasNoVino(c: Cita, nombre: string): string {
    const cargo = cargoNoShow(c);
    return cargo !== null
        ? `Marcada como no presentado. Avisamos a ${primerNombre(nombre)} y le cobramos ${dinero(cargo.amount)} de la garantía.`
        : 'Marcada como no presentado. El resto del hueco queda libre.';
}

/**
 * 9.3 · Texto del cobro de un «no vino» fallido. Tras el tercer intento rechazado ya no se reintenta (Pagos no admite un
 * cuarto, G318) y la campana lleva el aviso `citas.cobro_no_show_fallido` (G795).
 */
export function textoCobroFallido(intentos: number, motivoBanco: string | null | undefined): string {
    const banco = motivoBanco != null ? ` El banco dice: «${motivoBanco}».` : '';
    return intentos >= 3
        ? `Han fallado los 3 intentos.${banco} Ya no se vuelve a intentar: gestiónalo con el cliente. Lo hemos avisado en la campana al propietario, los administradores y el encargado; el aviso se quita si deshaces el «no vino» o al archivarlo.`
        : `${String(Math.max(1, intentos))} de 3 intentos.${banco} Si falla el tercero, te avisamos en la campana y queda para ti.`;
}
